import React from 'react';
import styled from '@emotion/styled';
import { isNumber, isObject } from 'lodash';

import { SmallText, Text } from 'ds/components/typography';
import { Button } from 'ds/components/button';
import { useLocale } from 'locale';
import { PocType, PoiId, PoiType, ResolutionPreference } from 'utils/entities';
import { State } from 'store/state';
import { ISOString } from 'locale/formatDate';
import { createSelector } from 'reselect';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { connect } from 'react-redux';
import { DEFAULT_PLACEHOLDER_IMAGE, processImgs } from 'utils/gallery';
import { BookmarkIconWrapper } from 'components/favorites/styled';
import FavoritesToggler, { FavouritesIconMode } from 'components/favorites/FavoritesToggler';
import {
  getAddress,
  getDefaultPrimaryParams,
  renderAddress,
  UnitCardPrimaryParams,
  unitPagePrimaryTranslateKeys,
} from 'utils/addressFormatter';
import { setContactAgentOpen } from 'store/state/app/actions';
import { ContactAgentModalSource, ContactModalOpenMeta } from 'store/state/app';
import { isBulletinPoiType, isProjectPoiType } from 'utils/marketplaceRoutes';
import { favoritesAndShortlistDictSelector } from 'store/state/domainData/selectors/favorites';


const LastContactCardWrapper = styled.div`
  margin-right: ${({ theme }) => theme.spacing(1)};
  margin-left: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
  margin-bottom: ${theme.spacing(3)};
  &:hover {
    & > div:first-of-type {
      box-shadow: ${theme.shadow.level3};
    }
    button {
      display: block;
    }
  }
  `}
`;

const LastContactApartmentWrapper = styled.div`
  display: flex;
  height: 84px;
  border: solid 1px ${({ theme }) => theme.colors.neutrals.grey7};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
`;

const LastContactCardImage = styled.div<{ imageURL: string }>`
  min-width: 84px;
  height: 100%;
  position: relative;
  background-size: cover;
  background-position: center;
  ${({ imageURL }) => imageURL ? `background-image: url("${imageURL}");` : ''};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LastContactCardText = styled.div`
  margin: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
`;

const LastContactCardPrice = styled.div`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

const LastContactCardAddress = styled.div`
  color: ${({ theme }) => theme.colors.neutrals.grey3};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  white-space: nowrap;
`;

const LastContactCardAddressText = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  text-transform: capitalize;
`;

const LastContactCardAgentWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  align-items: center;
`;

const LastContactCardAgentInfo = styled.div`
  margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ theme }) => theme.spacing(1)};
  white-space: nowrap;
  flex-grow: 1;
  min-width: 0;
`;

const LastContactCardAgentName = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey2};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastContactCardAgentContacted = styled.div`
  color: ${({ theme }) => theme.colors.neutrals.grey4};
`;

const LastContactCardButtonWrapper = styled.div`
  flex-shrink: 0;
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    button {
      display: none;
    }
  `}
`;

export interface LastContactCardProps {
  id: PoiId;
  type: PoiType;
  projectName: string;
  cardImageURL?: string;
  price?: number | { min: number, max: number };
  streetNumber?: string;
  neighbourhood?: string;
  resolutionPreferences?: ResolutionPreference;
  streetName?: string;
  unitNumber?: string;
  agentName?: string;
  agentLastContactedDate?: ISOString;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
}

export const LastContactCard: React.FC<LastContactCardProps> = ({
  id,
  type,
  projectName,
  cardImageURL,
  price,
  streetNumber,
  streetName,
  unitNumber,
  neighbourhood,
  resolutionPreferences,
  agentName,
  agentLastContactedDate,
  onSetContactModalPoiId,
}) => {
  const { t, formatMoney, formatDate } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const imageURL = constructImageURI(cardImageURL || DEFAULT_PLACEHOLDER_IMAGE, { height: 84, width: 84 });

  const followUpHandler = () => onSetContactModalPoiId(id, { source: ContactAgentModalSource.ContactSection });
  const address = getAddress<UnitCardPrimaryParams>(t, renderAddress)({
    streetName,
    streetNumber,
    neighbourhood,
    unitNumber,
  }, unitPagePrimaryTranslateKeys, resolutionPreferences, getDefaultPrimaryParams);

  let formattedPrice = null;

  if (isNumber(price)) {
    formattedPrice = price ? formatMoney(price) : null;
  }
  else if (isObject(price)) {
    formattedPrice = price.min === price.max
      ? (price.min ? formatMoney(price.min) : projectName)
      : `${formatMoney(price.min)} - ${formatMoney(price.max)}`;
  }

  return (
    <LastContactCardWrapper>
      <LastContactApartmentWrapper>
        <LastContactCardImage
          imageURL={imageURL}
        >
          <BookmarkIconWrapper>
            <FavoritesToggler id={id} type={type} iconMode={FavouritesIconMode.White} />
          </BookmarkIconWrapper>
        </LastContactCardImage>
        <LastContactCardText>
          <LastContactCardPrice>
            <Text>{formattedPrice}</Text>
          </LastContactCardPrice>
          <LastContactCardAddress>
            <LastContactCardAddressText>
              {address}
            </LastContactCardAddressText>
          </LastContactCardAddress>
        </LastContactCardText>
      </LastContactApartmentWrapper>
      <LastContactCardAgentWrapper>
        <LastContactCardAgentInfo>
          <LastContactCardAgentName weight="medium">
            {agentName}
          </LastContactCardAgentName>
          <LastContactCardAgentContacted>
            <SmallText>
              {t('shortlist.lastContact.agentLastContacted', { agentLastContactedDate: formatDate(agentLastContactedDate) })}
            </SmallText>
          </LastContactCardAgentContacted>
        </LastContactCardAgentInfo>
        <LastContactCardButtonWrapper>
          <Button
            size="small"
            onClick={followUpHandler}
          >
            {t('shortlist.lastContact.follow')}
          </Button>
        </LastContactCardButtonWrapper>
      </LastContactCardAgentWrapper>
    </LastContactCardWrapper>
  );
};

export const getPoiById = (state: State, poiId: PoiId) => favoritesAndShortlistDictSelector(state)[poiId];

export const makePoiIdSelector = () => createSelector([
  getPoiById,
], poi => poi.id);

export const makeProjectNameSelector = () => createSelector([
  getPoiById,
], poi => isProjectPoiType(poi) ? poi.projectName : '');

export const makePoiTypeSelector = () => createSelector([
  getPoiById,
], poi => poi.type);

export const makePoiImagesSelector = () => createSelector([
  getPoiById,
], (poi) => {
  if (isBulletinPoiType(poi)) {
    const img = processImgs(poi.images)[0];
    return img ? img.url : null;
  }
  else if (isProjectPoiType(poi)) {
    const img = (poi.images || [])[0];
    return img ? img.path : null;
  }
  return null;
});

const makePriceSelector = () => createSelector([
  getPoiById,
], (poi) => {
  if (isBulletinPoiType(poi)) {
    return poi.price;
  }
  else if (isProjectPoiType(poi)) {
    return poi.priceRange;
  }
  return null;
});

const makeStreetNumberSelector = () => createSelector([
  getPoiById,
], poi => poi.addressDetails.streetNumber);

const makeStreetNameSelector = () => createSelector([
  getPoiById,
], poi => poi.addressDetails.streetName);

const makeUnitNumberSelector = () => createSelector([
  getPoiById,
], poi => poi.addressDetails.unitNumber);

const makeNeighbourhoodSelector = () => createSelector([
  getPoiById,
], poi => poi.addressDetails.neighbourhood);

const makeResolutionPreferencesSelector = () => createSelector([
  getPoiById,
], poi => poi.addressDetails.resolutionPreferences);

const makeAgentNameSelector = () => createSelector([
  getPoiById,
], (poi) => {
  if (isBulletinPoiType(poi)) {
    const { poc } = poi;
    if (poc.type === PocType.Agent) {
      return poc.name || poc.agentContact.name || poc.company;
    }
    return poc.contactInfo.name;
  }
  else if (isProjectPoiType(poi)) {
    return poi.developers.length ? poi.developers[0].name : '';
  }
  return null;
});

const makeMapStateToProps = () => {

  const poiIdSelector = makePoiIdSelector();
  const projectNameSelector = makeProjectNameSelector();
  const poiTypeSelector = makePoiTypeSelector();
  const poiImageUrlSelector = makePoiImagesSelector();
  const priceSelector = makePriceSelector();
  const streetNumberSelector = makeStreetNumberSelector();
  const streetNameSelector = makeStreetNameSelector();
  const unitNumberSelector = makeUnitNumberSelector();
  const agentNameSelector = makeAgentNameSelector();
  const neighbourhoodSelector = makeNeighbourhoodSelector();
  const resolutionPreferencesSelector = makeResolutionPreferencesSelector();

  return (state: State, props: { poiId: PoiId, date: ISOString }) => {
    return {
      id: poiIdSelector(state, props.poiId),
      projectName: projectNameSelector(state, props.poiId),
      type: poiTypeSelector(state, props.poiId),
      cardImageURL: poiImageUrlSelector(state, props.poiId),
      price: priceSelector(state, props.poiId),
      streetNumber: streetNumberSelector(state, props.poiId),
      streetName: streetNameSelector(state, props.poiId),
      neighbourhood: neighbourhoodSelector(state, props.poiId),
      resolutionPreferences: resolutionPreferencesSelector(state, props.poiId),
      unitNumber: unitNumberSelector(state, props.poiId),
      agentName: agentNameSelector(state, props.poiId),
      agentLastContactedDate: props.date,
    };
  };
};

export default connect(makeMapStateToProps, { onSetContactModalPoiId: setContactAgentOpen })(LastContactCard);
